import { useEffect, useState } from 'react';
import { SkeletonRow } from '../Skeleton';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api';

/**
 * Collaborators (35i #5) — ported to match Sparked v2's studio share flow: a toolbar button that opens a
 * "Manage collaborators" modal (invite by email → PENDING → copy invite link → revoke). The AI thread is
 * already shared across everyone on the project. Email sending is a follow-up; the copy-link works today.
 */

interface Person {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  status: 'accepted' | 'pending';
  access: 'owner' | 'read' | 'write' | 'invite';
  inviteToken: string | null;
}

const UsersIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

export function CollaboratorsButton({ projectId, meId }: { projectId: string; meId?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-xl border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink transition hover:border-accent/50 hover:text-accent-deep"
      >
        <UsersIcon /> Collaborators
      </button>
      {open && <CollaboratorsModal projectId={projectId} meId={meId} onClose={() => setOpen(false)} />}
    </>
  );
}

function CollaboratorsModal({ projectId, meId, onClose }: { projectId: string; meId?: string; onClose: () => void }) {
  const [list, setList] = useState<Person[] | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = () =>
    api
      .get<{ collaborators: Person[] }>(`/box/projects/${projectId}/collaborators`)
      .then(d => setList(d.collaborators))
      .catch(() => setList([]));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const inviteLink = (token: string) => `${window.location.origin}/projects/${projectId}?invite=${token}`;
  const copyLink = (token: string) => {
    void navigator.clipboard?.writeText(inviteLink(token)).then(
      () => setNote('Invite link copied'),
      () => setNote('Copy failed'),
    );
  };

  const submit = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const { token, reused } = await api.post<{ token: string; reused?: boolean }>(`/box/projects/${projectId}/collaborators`, { email: email.trim() });
      setEmail('');
      copyLink(token);
      setNote(reused ? 'Already invited — link copied' : 'Invite created — link copied');
      await load();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Could not invite');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setList(l => (l ?? []).filter(p => p.id !== id));
    await api.delete(`/box/projects/${projectId}/collaborators/${id}`).catch(() => void load());
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/45 backdrop-blur-sm" onClick={onClose} />
      <div className="anim-in relative w-full max-w-md overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-line px-5 py-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-accent-deep">Studio · Share</p>
            <h3 className="text-lg font-bold text-ink">Manage collaborators</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-muted transition hover:text-ink">
            <XIcon />
          </button>
        </div>

        <div className="border-b border-line bg-page px-5 py-4">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">Invite by email</label>
          <div className="flex gap-2">
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submit();
                }
              }}
              type="email"
              placeholder="name@example.com"
              className="h-10 flex-1 rounded-lg border border-line bg-surface px-3 text-sm outline-none focus:border-accent/60"
            />
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!email.trim() || busy}
              className="h-10 shrink-0 rounded-lg bg-accent px-4 text-sm font-bold text-accent-ink transition hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-muted">
            They&rsquo;ll appear as <span className="rounded bg-amber-100 px-1 font-bold text-amber-700">PENDING</span> until they accept. Email sending is coming soon — for now, <b>copy the invite link</b> and share it.
          </p>
          {note && <p className="mt-1.5 text-[11px] font-semibold text-accent-deep">{note}</p>}
        </div>

        <div className="max-h-72 overflow-auto px-5 py-3">
          {/* shaped like the rows below (avatar + name + role) so nothing shifts when they land */}
          {list === null && (
            <div className="space-y-3 py-1" aria-busy="true" aria-label="Loading collaborators">
              <SkeletonRow />
              <SkeletonRow />
            </div>
          )}
          {list?.length === 0 && <p className="py-6 text-center text-sm italic text-muted">No collaborators yet. Send an invite above to get started.</p>}
          {list?.map(c => (
            <div key={c.id} className="flex items-center gap-2 border-b border-line/60 py-2 last:border-0">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[11px] font-bold uppercase text-accent-deep">{c.email.slice(0, 2)}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-ink">
                {c.name}
                {c.id === meId && <span className="ml-1 text-[11px] text-muted">(you)</span>}
              </span>
              <span className={['shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide', c.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'].join(' ')}>
                {c.access === 'owner' ? 'owner' : c.status}
              </span>
              {c.status === 'pending' && c.inviteToken && (
                <button onClick={() => copyLink(c.inviteToken!)} aria-label="Copy invite link" title="Copy invite link" className="shrink-0 rounded-lg border border-line p-1.5 text-muted transition hover:text-accent-deep">
                  <ShareIcon />
                </button>
              )}
              {c.access !== 'owner' && (
                <button onClick={() => void remove(c.id)} aria-label="Remove" title="Remove collaborator" className="shrink-0 rounded-lg border border-line p-1.5 text-muted transition hover:text-rose-600">
                  <XIcon />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
